/* eslint-disable react-hooks/rules-of-hooks */
import React from 'react';
import dayjs from 'dayjs';
import {
  Avatar,
  Button,
  Card,
  Col,
  DatePicker,
  Divider,
  Empty,
  Input,
  List,
  Modal,
  Row,
  Select,
  Space,
  Table,
  Typography,
  message,
} from 'antd';
import {
  BellOutlined,
  CommentOutlined,
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { formatDateTime, formatDate } from '../utils/formatters';
import { brandSemantic } from '../config/brand';

const { Text } = Typography;
const { TextArea } = Input;

const followUpStatusTone = {
  Done: { color: brandSemantic.success, background: brandSemantic.successSoft, border: brandSemantic.successBorder },
  'In Production': { color: brandSemantic.warning, background: brandSemantic.warningSoft, border: brandSemantic.warningBorder },
  Open: { color: brandSemantic.info, background: brandSemantic.infoSoft, border: brandSemantic.infoBorder },
  Pending: { color: brandSemantic.textSecondary, background: brandSemantic.primarySoft, border: brandSemantic.primaryMuted },
  'Not Done': { color: brandSemantic.error, background: brandSemantic.errorSoft, border: brandSemantic.errorBorder },
};

const followUpPriorityTone = {
  High: { color: brandSemantic.error, background: brandSemantic.errorSoft, border: brandSemantic.errorBorder },
  Medium: { color: brandSemantic.warning, background: brandSemantic.warningSoft, border: brandSemantic.warningBorder },
  Low: { color: brandSemantic.success, background: brandSemantic.successSoft, border: brandSemantic.successBorder },
};

const getFollowUpStatusSelectStyle = (status) => ({
  width: Math.min(Math.max(status.length * 7 + 34, 102), 148),
  '--status-color': (followUpStatusTone[status] || followUpStatusTone.Pending).color,
  '--status-bg': (followUpStatusTone[status] || followUpStatusTone.Pending).background,
  '--status-border': (followUpStatusTone[status] || followUpStatusTone.Pending).border,
});

const getFollowUpPrioritySelectStyle = (priority = 'Medium') => ({
  width: 112,
  '--status-color': (followUpPriorityTone[priority] || followUpPriorityTone.Medium).color,
  '--status-bg': (followUpPriorityTone[priority] || followUpPriorityTone.Medium).background,
  '--status-border': (followUpPriorityTone[priority] || followUpPriorityTone.Medium).border,
});

export default function FollowUpsModal({
  open,
  onCancel,
  viewingFollowUps,
  currentUser,
  users,
  items,
  setFollowUps,
  addingFollowUp,
  setAddingFollowUp,
  editingFollowUpId,
  setEditingFollowUpId,
  newFollowUp,
  setNewFollowUp,
  viewingFollowUpComments,
  setViewingFollowUpComments,
  newFollowUpComment,
  setNewFollowUpComment,
  getFollowUpComments,
  handleAddFollowUpComment,
  t,
  canDelete = true,
}) {
  if (!viewingFollowUps) return null;

  const statusCounts = {
    Done: items.filter((item) => item.status === 'Done').length,
    'In Production': items.filter((item) => item.status === 'In Production').length,
    Open: items.filter((item) => item.status === 'Open').length,
    Pending: items.filter((item) => item.status === 'Pending').length,
    'Not Done': items.filter((item) => item.status === 'Not Done').length,
  };

  const resetFollowUpEditor = () => {
    setNewFollowUp({
      date: '',
      action: '',
      responsible: currentUser?.name || '',
      priority: 'Medium',
      status: 'Pending',
    });
    setEditingFollowUpId(null);
    setAddingFollowUp(false);
  };

  const getFollowUpPickerValue = (date) => {
    if (!date) return null;
    const [day, month, year] = date.split('.');
    return day && month && year ? dayjs(`${year}-${month}-${day}`) : null;
  };

  const columns = [
    {
      title: t('Follow-up date'),
      dataIndex: 'date',
      key: 'date',
      width: 110,
      render: (value) => <strong>{formatDate(value)}</strong>,
    },
    { title: t('Description'), dataIndex: 'action', key: 'action' },
    { title: t('Responsible'), dataIndex: 'responsible', key: 'responsible', width: 120 },
    {
      title: t('Priority'),
      dataIndex: 'priority',
      key: 'priority',
      width: 120,
      render: (priority = 'Medium', record) => (
        <Select
          value={priority}
          onChange={(value) => {
            setFollowUps((prev) => prev.map((item) => (
              item.id === record.id ? { ...item, priority: value } : item
            )));
          }}
          size="small"
          variant="borderless"
          className="status-tag-select"
          style={getFollowUpPrioritySelectStyle(priority)}
          dropdownMatchSelectWidth={false}
          options={[
            { value: 'High', label: t('High') },
            { value: 'Medium', label: t('Medium') },
            { value: 'Low', label: t('Low') },
          ]}
        />
      ),
    },
    {
      title: t('Status'),
      dataIndex: 'status',
      key: 'status',
      width: 130,
      render: (status, record) => (
        <Select
          value={status}
          onChange={(value) => {
            setFollowUps((prev) => prev.map((item) => (
              item.id === record.id ? { ...item, status: value } : item
            )));
          }}
          size="small"
          variant="borderless"
          className="status-tag-select"
          style={getFollowUpStatusSelectStyle(status)}
          dropdownMatchSelectWidth={false}
          options={[
            { value: 'Pending', label: 'Pending' },
            { value: 'Open', label: 'Open' },
            { value: 'In Production', label: 'In Production' },
            { value: 'Done', label: 'Done' },
            { value: 'Not Done', label: 'Not Done' },
          ]}
        />
      ),
    },
    {
      title: t('Comments'),
      key: 'comments',
      render: (_, record) => {
        const commentItems = getFollowUpComments(record);
        if (commentItems.length === 0) {
          return (
            <Button
              type="link"
              size="small"
              icon={<PlusOutlined />}
              style={{ padding: 0, height: 'auto' }}
              onClick={() => setViewingFollowUpComments(record)}
            >
                  {t('Add')}
            </Button>
          );
        }
        return (
          <Button
            type="link"
            icon={<CommentOutlined />}
            style={{ padding: 0 }}
            onClick={() => setViewingFollowUpComments(record)}
          >
            {commentItems.length}
          </Button>
        );
      },
    },
    {
      title: t('Actions'),
      key: 'actions',
      width: 100,
      render: (_, record) => (
        <Space direction="vertical" size={2}>
          <Button
            type="link"
            icon={<EditOutlined />}
            style={{ padding: 0, height: 'auto' }}
            onClick={() => {
              setAddingFollowUp(true);
              setEditingFollowUpId(record.id);
              setNewFollowUp({
                date: record.date || '',
                action: record.action || '',
                responsible: record.responsible || '',
                priority: record.priority || 'Medium',
                status: record.status || 'Pending',
              });
            }}
          >
            {t('Edit')}
          </Button>
          {canDelete && (
            <Button
              type="link"
              danger
              icon={<DeleteOutlined />}
              style={{ padding: 0, height: 'auto' }}
              onClick={() => {
                setFollowUps((prev) => prev.filter((item) => item.id !== record.id));
                if (viewingFollowUpComments?.id === record.id) {
                  setViewingFollowUpComments(null);
                  setNewFollowUpComment('');
                }
                if (editingFollowUpId === record.id) {
                  resetFollowUpEditor();
                }
                message.success(t('Follow-up deleted'));
              }}
            >
              {t('Delete')}
            </Button>
          )}
        </Space>
      ),
    },
  ];

  return (
    <Modal
      title={(
        <Space>
          <BellOutlined style={{ color: brandSemantic.primary, fontSize: 16 }} />
          <strong>{t('Follow-ups')}</strong>
          <Text type="secondary" style={{ fontSize: 13, fontWeight: 400 }}>
            {viewingFollowUps.id} · {viewingFollowUps.client}
          </Text>
        </Space>
      )}
      open={open}
      onCancel={onCancel}
      footer={null}
      width={900}
    >
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => {
            if (addingFollowUp && !editingFollowUpId) {
              resetFollowUpEditor();
              return;
            }
            setAddingFollowUp(true);
            setEditingFollowUpId(null);
            setNewFollowUp({
              date: '',
              action: '',
              responsible: currentUser?.name || '',
              priority: 'Medium',
              status: 'Pending',
            });
          }}
        >
          {t('Add Follow-up')}
        </Button>
      </div>

      {addingFollowUp && (
        <Card size="small" style={{ marginBottom: 16, background: brandSemantic.layout, border: `1px solid ${brandSemantic.border}` }}>
          <Row gutter={12}>
            <Col span={5}>
              <DatePicker
                format="DD.MM.YYYY"
                style={{ width: '100%' }}
                value={getFollowUpPickerValue(newFollowUp.date)}
                placeholder={t('Select date')}
                onChange={(_, dateString) => setNewFollowUp((prev) => ({ ...prev, date: dateString }))}
              />
            </Col>
            <Col span={7}>
              <Input
                placeholder={t('Description')}
                value={newFollowUp.action}
                onChange={(event) => setNewFollowUp((prev) => ({ ...prev, action: event.target.value }))}
              />
            </Col>
            <Col span={4}>
              <Select
                value={newFollowUp.responsible || undefined}
                placeholder={t('Responsible')}
                onChange={(value) => setNewFollowUp((prev) => ({ ...prev, responsible: value }))}
                style={{ width: '100%' }}
                options={users.filter((user) => user.active).map((user) => ({ value: user.name, label: user.name }))}
              />
            </Col>
            <Col span={3}>
              <Select
                value={newFollowUp.priority || 'Medium'}
                onChange={(value) => setNewFollowUp((prev) => ({ ...prev, priority: value }))}
                style={{ width: '100%' }}
                options={[
                  { value: 'High', label: t('High') },
                  { value: 'Medium', label: t('Medium') },
                  { value: 'Low', label: t('Low') },
                ]}
              />
            </Col>
            <Col span={5}>
              <Space style={{ width: '100%' }} size={8}>
                <Button onClick={resetFollowUpEditor} style={{ flex: 1 }}>
                  {t('Close')}
                </Button>
                <Button
                  type="primary"
                  style={{ flex: 1 }}
                  onClick={() => {
                    if (!newFollowUp.date || !newFollowUp.action.trim() || !newFollowUp.responsible) {
                      message.warning(t('Date, description, and responsible are required'));
                      return;
                    }
                    if (editingFollowUpId) {
                      setFollowUps((prev) => prev.map((item) => (
                        item.id === editingFollowUpId ? { ...item, ...newFollowUp } : item
                      )));
                      if (viewingFollowUpComments?.id === editingFollowUpId) {
                        setViewingFollowUpComments((prev) => (prev ? { ...prev, ...newFollowUp } : prev));
                      }
                      resetFollowUpEditor();
                      message.success(t('Follow-up updated'));
                      return;
                    }
                    setFollowUps((prev) => [
                      ...prev,
                      { ...newFollowUp, id: Date.now(), offerId: viewingFollowUps.id, reminder: '', comments: [] },
                    ]);
                    resetFollowUpEditor();
                    message.success(t('Follow-up added'));
                  }}
                >
                  {editingFollowUpId ? t('Update') : t('Save')}
                </Button>
              </Space>
            </Col>
          </Row>
        </Card>
      )}

      <Table dataSource={items} columns={columns} rowKey="id" pagination={false} bordered size="middle" />

      <div style={{ marginTop: 16, padding: '10px 16px', background: brandSemantic.layout, borderRadius: 8, display: 'flex', gap: 24, flexWrap: 'wrap', fontSize: 13 }}>
        <span><strong>{t('Total: {{count}} follow-ups', { count: items.length })}</strong></span>
        {[
          ['Done', brandSemantic.success],
          ['In Production', brandSemantic.warning],
          ['Open', brandSemantic.info],
          ['Pending', brandSemantic.textSecondary],
          ['Not Done', brandSemantic.error],
        ].map(([status, color]) => (
          <span key={status} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ width: 8, height: 8, borderRadius: '50%', background: color, display: 'inline-block' }} />
            {t(status)}: {statusCounts[status]}
          </span>
        ))}
      </div>

      <Modal
        title={t('Comments')}
        open={Boolean(viewingFollowUpComments)}
        onCancel={() => {
          setViewingFollowUpComments(null);
          setNewFollowUpComment('');
        }}
        footer={null}
        width={760}
        destroyOnClose
      >
        <div style={{ marginBottom: 16 }}>
          <TextArea
            rows={3}
            placeholder={t('Add a new comment...')}
            value={newFollowUpComment}
            onChange={(event) => setNewFollowUpComment(event.target.value)}
            style={{ marginBottom: 8 }}
          />
          <Button type="primary" onClick={() => handleAddFollowUpComment(viewingFollowUpComments?.id, newFollowUpComment)}>
            {t('Add Comment')}
          </Button>
        </div>
        <Divider />
        {viewingFollowUpComments && getFollowUpComments(viewingFollowUpComments).length === 0 ? (
          <Empty description={t('No comments yet')} image={Empty.PRESENTED_IMAGE_SIMPLE} />
        ) : (
          <List
            dataSource={viewingFollowUpComments ? [...getFollowUpComments(viewingFollowUpComments)].sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp)) : []}
            renderItem={(comment) => (
              <List.Item>
                <List.Item.Meta
                  avatar={<Avatar icon={<UserOutlined />} />}
                  title={(
                    <Space>
                      <strong>{comment.author}</strong>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {formatDateTime(comment.timestamp)}
                      </Text>
                    </Space>
                  )}
                  description={comment.comment}
                />
              </List.Item>
            )}
          />
        )}
      </Modal>
    </Modal>
  );
}
